import { useState } from 'react';
import { useMutation, useAction } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: components in nested directory
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import type { IInviteCompanyDialogProps } from '@/@types/company';

export function InviteCompanyDialog({ open, onOpenChange, orgId }: IInviteCompanyDialogProps) {
  const [companyName, setCompanyName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createCompany = useMutation(api['companies/mutations'].create);
  const sendInvitation = useAction(api['companies/invitationActions'].sendCompanyInvitation);

  const handleOpenChange = (value: boolean) => {
    if (!value) {
      setCompanyName('');
      setAdminEmail('');
    }
    onOpenChange(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyName.trim() || !adminEmail.trim()) return;

    setIsSubmitting(true);
    try {
      const companyId = await createCompany({
        orgId,
        name: companyName.trim(),
        email: adminEmail.trim().toLowerCase(),
      });
      await sendInvitation({
        companyId,
        email: adminEmail.trim().toLowerCase(),
        roles: ['admin'],
      });
      toast.success('Company created and invitation sent');
      handleOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create company');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Company</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="company-name">Company Name</Label>
            <Input
              id="company-name"
              placeholder="Enter company name"
              value={companyName}
              onChange={e => setCompanyName(e.target.value)}
              disabled={isSubmitting}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="admin-email">Admin Email</Label>
            <Input
              id="admin-email"
              type="email"
              placeholder="admin@company.com"
              value={adminEmail}
              onChange={e => setAdminEmail(e.target.value)}
              disabled={isSubmitting}
              required
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !companyName.trim() || !adminEmail.trim()}
            >
              {isSubmitting ? (
                <>
                  <Loader2 aria-hidden="true" className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create & Invite'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
